import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, renderList as _renderList, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "bsdd-modal" }
const _hoisted_2 = { class: "bsdd-modal__header" }
const _hoisted_3 = {
  key: 0,
  class: "bsdd-modal__subtitle"
}
const _hoisted_4 = { class: "bsdd-modal__body" }
const _hoisted_5 = {
  key: 0,
  class: "bsdd-modal__state"
}
const _hoisted_6 = {
  key: 1,
  class: "bsdd-modal__state bsdd-modal__state--error"
}
const _hoisted_7 = { class: "bsdd-section" }
const _hoisted_8 = { class: "bsdd-row" }
const _hoisted_9 = { class: "bsdd-row__val" }
const _hoisted_10 = { class: "bsdd-row" }
const _hoisted_11 = { class: "bsdd-row__val" }
const _hoisted_12 = {
  key: 0,
  class: "bsdd-row"
}
const _hoisted_13 = { class: "bsdd-row__val" }
const _hoisted_14 = {
  key: 1,
  class: "bsdd-row"
}
const _hoisted_15 = { class: "bsdd-row__val" }
const _hoisted_16 = ["href"]
const _hoisted_17 = {
  key: 0,
  class: "bsdd-section"
}
const _hoisted_18 = { class: "bsdd-chips" }
const _hoisted_19 = ["href", "title"]
const _hoisted_20 = { class: "bsdd-chip__dict" }
const _hoisted_21 = { class: "bsdd-chip__code" }
const _hoisted_22 = {
  key: 1,
  class: "bsdd-section"
}
const _hoisted_23 = { class: "bsdd-definition" }
const _hoisted_24 = {
  key: 2,
  class: "bsdd-section"
}
const _hoisted_25 = { class: "bsdd-hierarchy" }
const _hoisted_26 = {
  key: 0,
  class: "bsdd-hierarchy__arrow"
}
const _hoisted_27 = { class: "bsdd-section__title" }
const _hoisted_28 = { class: "bsdd-props" }
const _hoisted_29 = { class: "bsdd-props__code" }
const _hoisted_30 = {
  key: 0,
  class: "bsdd-unit"
}
const _hoisted_31 = {
  key: 0,
  class: "bsdd-value bsdd-value--ok"
}
const _hoisted_32 = {
  key: 1,
  class: "bsdd-value bsdd-value--warn",
  title: 'Value not in allowed set'
}
const _hoisted_33 = {
  key: 1,
  class: "bsdd-value bsdd-value--missing"
}
const _hoisted_34 = ["title"]
const _hoisted_35 = {
  key: 1,
  class: "bsdd-allowed-none"
}

import { ref, onMounted } from 'vue';
    import { Request } from 'Core/Common/Request';
    import type { IfcNodeDto } from 'api/IfcParser/Application/Models/IfcNodeDto';
    import type { IfcPropertySetDto } from 'api/IfcParser/Application/Models/IfcPropertySetDto';

    interface BsddClassProperty {
        name: string;
        propertyCode: string;
        propertyUri?: string;
        propertySet?: string;
        dataType?: string;
        allowedValues?: Array<{ value: string; description?: string }>;
        unit?: string | null;
    }

    interface BsddClassRelation {
        relationType: string;
        relatedClassUri: string;
        relatedClassName: string;
        dictionary?: string;
    }

    interface BsddClassInfo {
        name?: string;
        referenceCode?: string;
        uri?: string;
        dictionaryUri?: string;
        definition?: string;
        hierarchy?: Array<{ level: number; name: string; code: string; uri: string }>;
        classProperties?: BsddClassProperty[];
        classRelations?: BsddClassRelation[];
        status?: string;
    }

    
export default /*@__PURE__*/_defineComponent({
  __name: 'BsddInfoModal',
  props: {
    node: {}
  },
  setup(__props: any) {

    const props = __props;

    const CAMEL_CASE_HEADERS = { 'Accept-CamelCase': 'true' };

    /**
     * Look up a class definition in the buildingSMART Data Dictionary (bSDD).
     * Endpoint is on the IFC API controller — kept inline here so the modal
     * has no dependency on the IfcImporter page's full API client.
     */
    function fetchBsddClassInfo(ifcClass: string, ifcVersion: string = '4.3') {
        const url = `/api/IfcApi/GetBsddClassInfo?ifcClass=${encodeURIComponent(ifcClass)}&ifcVersion=${encodeURIComponent(ifcVersion)}`;
        return Request.Send({
            proxy: { url, type: 'GET', headers: CAMEL_CASE_HEADERS },
        });
    }

    const loading = ref(false);
    const error = ref('');
    const data = ref<BsddClassInfo | null>(null);

    // Flatten all actual property values from the file for cross-checking
    // against the canonical bSDD property list.
    const actualValues = (): Record<string, string> => {
        const map: Record<string, string> = {};
        if (!props.node) return map;
        for (const pset of props.node.propertySets || []) {
            for (const p of (pset as IfcPropertySetDto).properties || []) {
                map[`${pset.name}.${p.name}`.toLowerCase()] = String(p.value ?? '');
                map[p.name.toLowerCase()] = String(p.value ?? '');
            }
        }
        return map;
    };

    const fetchBsdd = async (ifcClass: string) => {
        loading.value = true;
        error.value = '';
        data.value = null;
        try {
            data.value = await new Promise<any>((resolve, reject) => {
                fetchBsddClassInfo(ifcClass).then(resolve).fail(reject);
            });
        } catch (err: any) {
            error.value = err?.responseJSON?.Message || err?.message || 'bSDD lookup failed.';
        } finally {
            loading.value = false;
        }
    };

    onMounted(() => {
        if (props.node?.ifcClass) fetchBsdd(props.node.ifcClass);
    });

    // Group bSDD properties by Pset for nicer rendering
    const groupedProperties = () => {
        if (!data.value?.classProperties) return {} as Record<string, BsddClassProperty[]>;
        const grouped: Record<string, BsddClassProperty[]> = {};
        for (const p of data.value.classProperties) {
            const key = p.propertySet || 'Other';
            (grouped[key] ||= []).push(p);
        }
        return grouped;
    };

    const getActualValue = (prop: BsddClassProperty): string | null => {
        const vals = actualValues();
        const keys = [
            `${prop.propertySet}.${prop.propertyCode}`.toLowerCase(),
            `${prop.propertySet}.${prop.name}`.toLowerCase(),
            prop.propertyCode.toLowerCase(),
            prop.name.toLowerCase(),
        ];
        for (const k of keys) if (vals[k]) return vals[k];
        return null;
    };

    const isValueAllowed = (prop: BsddClassProperty, actual: string | null): boolean => {
        if (!actual || !prop.allowedValues || prop.allowedValues.length === 0) return true;
        return prop.allowedValues.some((av) => av.value.toLowerCase() === actual.toLowerCase());
    };

    const crossClassifications = () => {
        if (!data.value?.classRelations) return [] as BsddClassRelation[];
        // Only show relations pointing to other dictionaries (Uniclass, OmniClass, ETIM …),
        // not IFC-internal IsA relations.
        return data.value.classRelations.filter((r) => r.dictionary && !/^IFC/i.test(r.dictionary));
    };

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _cache[0] || (_cache[0] = _createElementVNode("i", { class: "fa fa-book" }, null, -1 /* CACHED */)),
      _cache[1] || (_cache[1] = _createElementVNode("span", null, "buildingSMART Data Dictionary", -1 /* CACHED */)),
      (__props.node)
        ? (_openBlock(), _createElementBlock("span", _hoisted_3, "— " + _toDisplayString(__props.node.ifcClass), 1 /* TEXT */))
        : _createCommentVNode("v-if", true)
    ]),
    _createElementVNode("div", _hoisted_4, [
      (loading.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_5, [...(_cache[2] || (_cache[2] = [
            _createElementVNode("i", { class: "fa fa-spinner fa-spin" }, null, -1 /* CACHED */),
            _createTextVNode(" Looking up in bSDD… ", -1 /* CACHED */)
          ]))]))
        : (error.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
              _cache[3] || (_cache[3] = _createElementVNode("i", { class: "fa fa-exclamation-triangle" }, null, -1 /* CACHED */)),
              _createTextVNode(" " + _toDisplayString(error.value), 1 /* TEXT */)
            ]))
          : (data.value)
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                _createCommentVNode(" Top summary "),
                _createElementVNode("section", _hoisted_7, [
                  _createElementVNode("div", _hoisted_8, [
                    _cache[4] || (_cache[4] = _createElementVNode("span", { class: "bsdd-row__key" }, "Standard name", -1 /* CACHED */)),
                    _createElementVNode("span", _hoisted_9, _toDisplayString(data.value.name), 1 /* TEXT */)
                  ]),
                  _createElementVNode("div", _hoisted_10, [
                    _cache[5] || (_cache[5] = _createElementVNode("span", { class: "bsdd-row__key" }, "Reference code", -1 /* CACHED */)),
                    _createElementVNode("span", _hoisted_11, [
                      _createElementVNode("code", null, _toDisplayString(data.value.referenceCode), 1 /* TEXT */)
                    ])
                  ]),
                  (data.value.status)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                        _cache[6] || (_cache[6] = _createElementVNode("span", { class: "bsdd-row__key" }, "Status", -1 /* CACHED */)),
                        _createElementVNode("span", _hoisted_13, [
                          _createElementVNode("span", {
                            class: _normalizeClass(["bsdd-badge", `bsdd-badge--${data.value.status?.toLowerCase()}`])
                          }, _toDisplayString(data.value.status), 3 /* TEXT, CLASS */)
                        ])
                      ]))
                    : _createCommentVNode("v-if", true),
                  (data.value.uri)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
                        _cache[8] || (_cache[8] = _createElementVNode("span", { class: "bsdd-row__key" }, "Canonical URI", -1 /* CACHED */)),
                        _createElementVNode("span", _hoisted_15, [
                          _createElementVNode("a", {
                            href: data.value.uri,
                            target: "_blank",
                            rel: "noopener"
                          }, [
                            _createTextVNode(_toDisplayString(data.value.uri) + " ", 1 /* TEXT */),
                            _cache[7] || (_cache[7] = _createElementVNode("i", { class: "fa fa-external-link" }, null, -1 /* CACHED */))
                          ], 8 /* PROPS */, _hoisted_16)
                        ])
                      ]))
                    : _createCommentVNode("v-if", true)
                ]),
                _createCommentVNode(" Cross-classifications "),
                (crossClassifications().length > 0)
                  ? (_openBlock(), _createElementBlock("section", _hoisted_17, [
                      _cache[9] || (_cache[9] = _createElementVNode("h5", { class: "bsdd-section__title" }, "Cross-dictionary mappings", -1 /* CACHED */)),
                      _createElementVNode("div", _hoisted_18, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(crossClassifications(), (rel) => {
                          return (_openBlock(), _createElementBlock("a", {
                            key: rel.relatedClassUri,
                            class: "bsdd-chip",
                            href: rel.relatedClassUri,
                            target: "_blank",
                            rel: "noopener",
                            title: rel.relatedClassUri
                          }, [
                            _createElementVNode("span", _hoisted_20, _toDisplayString(rel.dictionary), 1 /* TEXT */),
                            _createElementVNode("span", _hoisted_21, _toDisplayString(rel.relatedClassName), 1 /* TEXT */)
                          ], 8 /* PROPS */, _hoisted_19))
                        }), 128 /* KEYED_FRAGMENT */))
                      ])
                    ]))
                  : _createCommentVNode("v-if", true),
                _createCommentVNode(" Definition "),
                (data.value.definition)
                  ? (_openBlock(), _createElementBlock("section", _hoisted_22, [
                      _cache[10] || (_cache[10] = _createElementVNode("h5", { class: "bsdd-section__title" }, "Definition", -1 /* CACHED */)),
                      _createElementVNode("p", _hoisted_23, _toDisplayString(data.value.definition), 1 /* TEXT */)
                    ]))
                  : _createCommentVNode("v-if", true),
                _createCommentVNode(" Hierarchy "),
                (data.value.hierarchy && data.value.hierarchy.length > 0)
                  ? (_openBlock(), _createElementBlock("section", _hoisted_24, [
                      _cache[11] || (_cache[11] = _createElementVNode("h5", { class: "bsdd-section__title" }, "IFC hierarchy", -1 /* CACHED */)),
                      _createElementVNode("div", _hoisted_25, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(data.value.hierarchy, (h, i) => {
                          return (_openBlock(), _createElementBlock(_Fragment, {
                            key: h.code
                          }, [
                            (i > 0)
                              ? (_openBlock(), _createElementBlock("span", _hoisted_26, "›"))
                              : _createCommentVNode("v-if", true),
                            _createElementVNode("span", {
                              class: _normalizeClass(["bsdd-hierarchy__node", {
                                    'bsdd-hierarchy__node--leaf': i === data.value.hierarchy!.length - 1,
                                }])
                            }, _toDisplayString(h.code), 3 /* TEXT, CLASS */)
                          ], 64 /* STABLE_FRAGMENT */))
                        }), 128 /* KEYED_FRAGMENT */))
                      ])
                    ]))
                  : _createCommentVNode("v-if", true),
                _createCommentVNode(" Canonical properties, grouped by Pset "),
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(groupedProperties(), (psetProps, psetName) => {
                  return (_openBlock(), _createElementBlock("section", {
                    key: psetName,
                    class: "bsdd-section"
                  }, [
                    _createElementVNode("h5", _hoisted_27, [
                      _createTextVNode(_toDisplayString(psetName) + " ", 1 /* TEXT */),
                      _cache[12] || (_cache[12] = _createElementVNode("span", { class: "bsdd-section__meta" }, "(spec from bSDD)", -1 /* CACHED */))
                    ]),
                    _createElementVNode("table", _hoisted_28, [
                      _cache[15] || (_cache[15] = _createElementVNode("thead", null, [
                        _createElementVNode("tr", null, [
                          _createElementVNode("th", null, "Property"),
                          _createElementVNode("th", null, "Type"),
                          _createElementVNode("th", null, "Actual value"),
                          _createElementVNode("th", null, "Allowed values")
                        ])
                      ], -1 /* CACHED */)),
                      _createElementVNode("tbody", null, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(psetProps, (p) => {
                          return (_openBlock(), _createElementBlock("tr", {
                            key: p.propertyCode
                          }, [
                            _createElementVNode("td", null, [
                              _createElementVNode("strong", null, _toDisplayString(p.name), 1 /* TEXT */),
                              _createElementVNode("div", _hoisted_29, _toDisplayString(p.propertyCode), 1 /* TEXT */)
                            ]),
                            _createElementVNode("td", null, [
                              _createTextVNode(_toDisplayString(p.dataType || '—') + " ", 1 /* TEXT */),
                              (p.unit)
                                ? (_openBlock(), _createElementBlock("span", _hoisted_30, _toDisplayString(p.unit), 1 /* TEXT */))
                                : _createCommentVNode("v-if", true)
                            ]),
                            _createElementVNode("td", null, [
                              (getActualValue(p) !== null)
                                ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                    (isValueAllowed(p, getActualValue(p)))
                                      ? (_openBlock(), _createElementBlock("span", _hoisted_31, [
                                          _cache[13] || (_cache[13] = _createElementVNode("i", { class: "fa fa-check" }, null, -1 /* CACHED */)),
                                          _createTextVNode(" " + _toDisplayString(getActualValue(p)), 1 /* TEXT */)
                                        ]))
                                      : (_openBlock(), _createElementBlock("span", _hoisted_32, [
                                          _cache[14] || (_cache[14] = _createElementVNode("i", { class: "fa fa-exclamation-triangle" }, null, -1 /* CACHED */)),
                                          _createTextVNode(" " + _toDisplayString(getActualValue(p)), 1 /* TEXT */)
                                        ]))
                                  ], 64 /* STABLE_FRAGMENT */))
                                : (_openBlock(), _createElementBlock("span", _hoisted_33, "—"))
                            ]),
                            _createElementVNode("td", null, [
                              (p.allowedValues && p.allowedValues.length > 0)
                                ? (_openBlock(true), _createElementBlock(_Fragment, { key: 0 }, _renderList(p.allowedValues, (av) => {
                                    return (_openBlock(), _createElementBlock("span", {
                                      key: av.value,
                                      class: "bsdd-allowed",
                                      title: av.description
                                    }, _toDisplayString(av.value), 9 /* TEXT, PROPS */, _hoisted_34))
                                  }), 128 /* KEYED_FRAGMENT */))
                                : (_openBlock(), _createElementBlock("span", _hoisted_35, "—"))
                            ])
                          ]))
                        }), 128 /* KEYED_FRAGMENT */))
                      ])
                    ])
                  ]))
                }), 128 /* KEYED_FRAGMENT */))
              ], 64 /* STABLE_FRAGMENT */))
            : _createCommentVNode("v-if", true)
    ])
  ]))
}
}

})